import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

export const GiftCardsHeroSkeleton = () => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        mb: 4,
      }}
    >
      <Grid container spacing={3.5} alignItems="center">
        <Grid item xs={12} lg={6}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: borderRadiusPx.md }} />
              <Skeleton variant="text" width={240} height={40} />
            </Stack>
            <Skeleton variant="text" width="95%" height={24} />
            <Skeleton variant="text" width="80%" height={24} />
          </Stack>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={6} sm={3} lg={6} key={i}>
                <Skeleton variant="rounded" height={95} sx={{ borderRadius: borderRadiusPx.lg }} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

export const GiftCardItemSkeleton = () => {
  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Skeleton variant="rectangular" height={160} width="100%" />
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Skeleton variant="text" width="40%" height={18} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="75%" height={36} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={44} sx={{ borderRadius: borderRadiusPx.md, mb: 2 }} />
        <Skeleton variant="text" width="90%" height={18} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="65%" height={18} sx={{ mb: 2 }} />
        <Box sx={{ mt: "auto", pt: 1 }}>
          <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: borderRadiusPx.md }} />
        </Box>
      </Box>
    </Card>
  );
};

export const GiftCardsTableSkeleton = ({ rows = 5 }) => {
  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.slate[50] }}>
              <TableCell><Skeleton variant="text" width={100} height={24} /></TableCell>
              <TableCell><Skeleton variant="text" width={70} height={24} /></TableCell>
              <TableCell><Skeleton variant="text" width={90} height={24} /></TableCell>
              <TableCell><Skeleton variant="text" width={70} height={24} /></TableCell>
              <TableCell><Skeleton variant="text" width={90} height={24} /></TableCell>
              <TableCell><Skeleton variant="text" width={90} height={24} /></TableCell>
              <TableCell align="right"><Skeleton variant="text" width={80} height={24} sx={{ ml: "auto" }} /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: rows }).map((_, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Skeleton variant="rounded" width={44} height={44} sx={{ borderRadius: borderRadiusPx.md }} />
                    <Box>
                      <Skeleton variant="text" width={80} height={20} />
                      <Skeleton variant="text" width={120} height={16} />
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell><Skeleton variant="rounded" width={85} height={24} sx={{ borderRadius: borderRadiusPx.pill }} /></TableCell>
                <TableCell><Skeleton variant="text" width={90} height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width={80} height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width={100} height={24} /></TableCell>
                <TableCell><Skeleton variant="text" width={95} height={24} /></TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Skeleton variant="rounded" width={80} height={32} sx={{ borderRadius: borderRadiusPx.md }} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

GiftCardsTableSkeleton.propTypes = {
  rows: PropTypes.number,
};

export const GiftCardsGridSkeleton = ({ count = 6, viewMode = "grid" }) => {
  if (viewMode === "table") {
    return <GiftCardsTableSkeleton rows={count} />;
  }

  return (
    <Grid container spacing={3}>
      {Array.from({ length: count }).map((_, idx) => (
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          key={idx}
        >
          <GiftCardItemSkeleton />
        </Grid>
      ))}
    </Grid>
  );
};

GiftCardsGridSkeleton.propTypes = {
  count: PropTypes.number,
  viewMode: PropTypes.oneOf(["grid", "table"]),
};

const GiftCardsSkeleton = {
  GiftCardsHeroSkeleton,
  GiftCardItemSkeleton,
  GiftCardsTableSkeleton,
  GiftCardsGridSkeleton,
};

export default GiftCardsSkeleton;
